import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container, Typography, IconButton, Paper, Box, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import useBack from '../hooks/useBack';
import { useAppContext } from '../components/AppContext'; // Importem useAppContext
import { ConcepteIcon } from '../components/common/IconMap.js'; // Importem ConcepteIcon
import config from '../config';

const SincroBancariaDetallPage = () => {
    const { userapp, id: executionIdParam, connectorId: connectorIdParam } = useParams();
    const executionId = executionIdParam ? parseInt(executionIdParam, 10) : null;
    const { sincroExecucionsData, ctxUser } = useAppContext(); // Obtenim les dades del context i l'usuari
    const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
    const { onBack } = useBack();
    const navigate = useNavigate();

    const [movements, setMovements] = useState([]);
    const [pageTitle, setPageTitle] = useState("Detall Sincronització");
    const [pageSubTitle, setPageSubTitle] = useState("Carregant detalls...");
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncErrorMessage, setSyncErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        setMovements([]);
        setSyncStatus(null);
        setSyncErrorMessage(null);

        if (!executionId) {
            setError("ID d'execució no proporcionat a la URL.");
            setPageTitle("Error");
            setPageSubTitle("No s'ha especificat cap execució.");
            setIsLoading(false);
            return;
        }

        const processExecution = (foundExecution) => {
            const execDate = new Date(foundExecution.itimestamp).toLocaleString();
            let currentMovements = [];

            if (connectorIdParam) {
                const foundConnector = (foundExecution.connectorResults || []).find(conn => conn.connectorId === connectorIdParam);
                if (foundConnector) {
                    currentMovements = (foundConnector.movimentsResult || []).map(mov => ({
                        ...mov,
                        uniqueKey: `${foundConnector.connectorId}-${mov.movId || Math.random()}`, // Clau única
                        connectorIdDisplay: foundConnector.connectorId // Per si es vol mostrar
                    }));
                    setPageTitle(`Detall Connector: ${connectorIdParam}`);
                    setPageSubTitle(`Execució ID: ${executionId} - ${execDate}`);
                    setSyncStatus(foundConnector.status || null);
                    setSyncErrorMessage(foundConnector.errorMessage || null);
                } else {
                    setError(`Connector ${connectorIdParam} no trobat per a l'execució ID ${executionId}.`);
                    setPageTitle("Error");
                    setPageSubTitle(`Connector no trobat`);
                }
            } else {
                // Detall global per l'execució
                (foundExecution.connectorResults || []).forEach(conn => {
                    (conn.movimentsResult || []).forEach(mov => {
                        currentMovements.push({
                            ...mov,
                            uniqueKey: `${conn.connectorId}-${mov.movId || Math.random()}`, // Clau única
                            connectorIdDisplay: conn.connectorId // Per mostrar a la taula
                        });
                    });
                });
                setPageTitle(`Detall Global Execució`);
                setPageSubTitle(`ID: ${executionId} - ${execDate}`);
                setSyncStatus(foundExecution.status || null);
                setSyncErrorMessage(foundExecution.errorMessage || null);
            }
            setMovements(currentMovements);
        };

        if (sincroExecucionsData && sincroExecucionsData.length > 0) {
            const foundExecution = sincroExecucionsData.find(exec => exec.id === executionId);
            if (foundExecution) {
                processExecution(foundExecution);
                setIsLoading(false);
                return;
            }
        }

        // Si no està al context o el context està buit, intentem carregar-lo de l'API /v1/resumexecucio/{id}
        if (jwtToken) {
            axios.get(`${config.apiBaseUrl}/v1/resumexecucio/${executionId}`, {
                headers: { Authorization: jwtToken }
            })
            .then(response => {
                if (response.data) {
                    processExecution(response.data);
                } else {
                    setError(`Execució amb ID ${executionId} no trobada.`);
                    setPageTitle("Error");
                    setPageSubTitle("Execució no trobada");
                }
            })
            .catch(err => {
                console.error("Error al carregar l'execució de sincronització:", err);
                setError(`Error al carregar les dades de l'execució ID ${executionId}.`);
                setPageTitle("Error");
                setPageSubTitle("Error de càrrega");
            })
            .finally(() => {
                setIsLoading(false);
            });
        } else {
            setError("Les dades de sincronització no estan disponibles al context. Si us plau, visiteu primer la pàgina de resum de sincronitzacions.");
            setPageTitle("Dades no disponibles");
            setPageSubTitle(`Per a l'execució ID: ${executionId}`);
            setIsLoading(false);
        }
    }, [sincroExecucionsData, executionId, connectorIdParam, jwtToken]);

    const handleBack = () => {
        onBack();
    };
    
    const handleRowClick = (movId) => {
        if (movId) {
            navigate(`/${userapp}/movimentdetail/${movId}`);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ marginTop: 4 }}>
            <Paper sx={{ padding: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
                    <IconButton onClick={handleBack} sx={{ marginRight: 1 }} aria-label="Tornar">
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="h5" component="h1">
                                {pageTitle}
                            </Typography>
                            {syncStatus && (
                                <Chip
                                    label={syncStatus}
                                    color={syncStatus === 'OK' ? 'success' : syncStatus === 'ERROR' ? 'error' : 'warning'}
                                    size="small"
                                />
                            )}
                        </Box>
                        <Typography variant="subtitle1" component="h2" color="text.secondary">
                            {pageSubTitle}
                        </Typography>
                    </Box>
                </Box>

                {syncStatus === 'ERROR' && (
                    <Alert severity="error" sx={{ marginY: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Error en la sincronització:
                        </Typography>
                        <Typography variant="body2">
                            {syncErrorMessage || "S'ha produït un error durant el procés de sincronització."}
                        </Typography>
                    </Alert>
                )}

                {syncStatus === 'PARCIAL' && (
                    <Alert severity="warning" sx={{ marginY: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Sincronització parcial:
                        </Typography>
                        <Typography variant="body2">
                            {syncErrorMessage || "La sincronització s'ha completat parcialment o amb advertències."}
                        </Typography>
                    </Alert>
                )}

                {isLoading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
                {error && <Alert severity="error" sx={{ marginY: 2 }}>{error}</Alert>}

                {!isLoading && !error && movements.length > 0 && (
                    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                        <Table stickyHeader aria-label="taula de moviments">
                            <TableHead> {/* El backgroundColor es mourà a les cel·les individuals */}
                                <TableRow sx={{
                                    borderBottom: '2px solid #ddd',
                                    '& th': { // Aplica a totes les TableCell dins d'aquest TableRow de capçalera
                                        backgroundColor: '#f0f0f0', // Assegura el fons per a cada cel·la
                                        fontWeight: 'bold',
                                        color: '#000',
                                        borderBottom: 'none' // Manté la vora inferior única del TableRow
                                    }
                                }}>
                                    {!connectorIdParam && <TableCell>Connector</TableCell>}
                                    <TableCell>Moviment</TableCell>
                                    <TableCell>Concepte Assignat</TableCell>
                                    <TableCell>Auto-Assignat</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {movements.map((mov) => (
                                    <TableRow
                                        hover
                                        key={mov.uniqueKey}
                                        onClick={() => handleRowClick(mov.movId)}
                                        sx={{ cursor: mov.movId ? 'pointer' : 'default' }}>
                                        {!connectorIdParam && <TableCell>{mov.connectorIdDisplay}</TableCell>}
                                        <TableCell>{mov.moviment}</TableCell>
                                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {mov.concepte && <ConcepteIcon concept={mov.concepte} />}
                                        </TableCell>
                                        <TableCell>{mov.autoAssigned ? 'Sí' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!isLoading && !error && movements.length === 0 && !error && ( // No mostrar si ja hi ha un error
                    <Typography sx={{ marginTop: 3, textAlign: 'center', fontStyle: 'italic' }}>
                        No hi ha moviments per mostrar per a la selecció actual.
                    </Typography>
                )}
            </Paper>
        </Container>
    );
};

export default SincroBancariaDetallPage;
